import { RefObject, useEffect, useRef, useState } from 'react';

import Text from '@cartwhl/ui/base/Text';
import Tooltip from '@cartwhl/ui/atoms/Tooltip';
import PenIcon from '@cartwhl/ui/icons/PenIcon';
import Button from '@cartwhl/ui/input/Button';

import { animationGenerationStore, historyStore, projectStore } from 'stores';

export default function EditableSessionName({
  jobID,
  sessionName,
  ref,
  isPoseProject = false,
}: {
  jobID: string;
  sessionName: string;
  ref?: RefObject<HTMLDivElement>;
  isPoseProject?: boolean;
}) {
  const [isEditing, setIsEditing] = useState(false);
  const [name, setName] = useState(sessionName);

  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setName(sessionName);
  }, [sessionName]);

  const handleClick = () => {
    setIsEditing(true);
  };

  const handleBlur = async () => {
    setIsEditing(false);
    if (name.trim() !== '') {
      if (isPoseProject) {
        await projectStore.updateProjectName(jobID, name);
        setName(sessionName);
      } else {
        await historyStore.setSessionName(jobID, name);
        animationGenerationStore.setSessionName(jobID, name);
      }
    } else {
      setName(sessionName);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleBlur();
    }
  };

  useEffect(() => {
    if (isEditing && inputRef.current && !inputRef.current.matches(':focus')) {
      inputRef.current.focus();
      inputRef.current.setSelectionRange(0, inputRef.current.value.length);
    }
  }, [inputRef.current, isEditing]);

  return (
    <div ref={ref} className="flex min-w-0 flex-1 items-center justify-center">
      {isEditing ? (
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          ref={inputRef}
          className="font-display outline-hidden w-full border-none bg-transparent text-center text-sm font-medium"
        />
      ) : (
        <Tooltip text={isPoseProject ? 'Rename project' : 'Rename session'}>
          <Button
            variant="tertiary"
            icon={!isEditing && <PenIcon className="text-gray-60" />}
            alignIcon="end"
            onClick={handleClick}
            className="max-w-full"
          >
            <Text variant="studio/title/md" className="truncate">
              {name}
            </Text>
          </Button>
        </Tooltip>
      )}
    </div>
  );
}
